import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, ChevronDown, LogOut, UserCircle, Settings, KeyRound, BrainCircuit, Activity, Gauge, Sparkles, Link2, MessageCircleMore, Moon, Sun, Users, BookOpen, ShieldCheck, Bot, Mail, Network, MonitorSmartphone } from 'lucide-react';
import NotificationBell from './NotificationBell';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../hooks/useAuth';
import { useWorkspace } from '../hooks/useWorkspace';

interface TopBarProps {
  onMenuClick: () => void;
}

export default function TopBar({ onMenuClick }: TopBarProps) {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { workspaces, currentWorkspace, switchWorkspace, loading, error } = useWorkspace();
  const navigate = useNavigate();
  const [wsDropdown, setWsDropdown] = useState(false);
  const [userDropdown, setUserDropdown] = useState(false);
  const wsRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (wsRef.current && !wsRef.current.contains(e.target as Node)) setWsDropdown(false);
      if (userRef.current && !userRef.current.contains(e.target as Node)) setUserDropdown(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <header
      className="glass-bar sticky top-0 z-20 box-border flex flex-none items-center justify-between px-4 lg:px-6"
      style={{
        height: 'calc(4rem + env(safe-area-inset-top))',
        paddingTop: 'env(safe-area-inset-top)',
      }}
    >
      <div className="flex items-center gap-3">
        <button
          onClick={onMenuClick}
          aria-label="Open navigation menu"
          className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-500 lg:hidden"
        >
          <Menu size={20} />
        </button>

        <div ref={wsRef} className="relative">
          <button
            onClick={() => setWsDropdown((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={wsDropdown}
            aria-label="Switch workspace"
            className="flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            <span className="flex h-6 w-6 items-center justify-center rounded bg-brand-600/20 text-xs font-bold text-brand-600 dark:text-brand-400" aria-hidden="true">
              {currentWorkspace?.name?.charAt(0).toUpperCase() || 'W'}
            </span>
            <span className="max-w-[140px] truncate">
              {loading ? 'Loading workspace…' : error ? 'Workspace unavailable' : currentWorkspace?.name || 'Select Workspace'}
            </span>
            <ChevronDown size={16} className="text-slate-600 dark:text-slate-400" aria-hidden="true" />
          </button>

          {wsDropdown && (
            <div role="menu" aria-label="Workspace switcher" className="dropdown-panel absolute left-0 mt-2 w-64 rounded-lg border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-800">
              <div className="max-h-64 overflow-y-auto p-1">
                {workspaces.length === 0 && (
                  <p className={`px-3 py-2 text-sm ${error ? 'text-red-600 dark:text-red-400' : 'text-slate-700 dark:text-slate-400'}`}>
                    {error || (loading ? 'Loading workspaces…' : 'No workspaces')}
                  </p>
                )}
                {workspaces.map((ws) => (
                  <button
                    key={ws.id}
                    onClick={() => { switchWorkspace(ws); setWsDropdown(false); }}
                    role="menuitem"
                    className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm cursor-pointer ${
                      ws.id === currentWorkspace?.id
                        ? 'bg-brand-50 text-brand-700 dark:bg-brand-600/20 dark:text-brand-400'
                        : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700'
                    }`}
                  >
                    <span className="flex h-6 w-6 items-center justify-center rounded bg-slate-200 text-xs font-bold dark:bg-slate-700" aria-hidden="true">
                      {ws.name.charAt(0).toUpperCase()}
                    </span>
                    {ws.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1">
        <NotificationBell />
        <div ref={userRef} className="relative">
          <button
            onClick={() => setUserDropdown((v) => !v)}
            aria-label="User menu"
            className="flex items-center gap-2 rounded-lg p-2 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-purple-600 text-sm font-bold text-white" aria-hidden="true">
              {user?.username?.charAt(0).toUpperCase() || 'U'}
            </div>
            <span className="hidden text-sm font-medium text-slate-700 dark:text-slate-200 sm:block">
              {user?.username || 'User'}
            </span>
            <ChevronDown size={16} className="text-slate-600 dark:text-slate-400" aria-hidden="true" />
          </button>

          {userDropdown && (
            <div role="menu" aria-label="User menu" className="dropdown-panel absolute right-0 mt-2 w-72 rounded-lg border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-800">
              {/* Settings tiles */}
              <div className="border-b border-slate-200 p-3 dark:border-slate-700">
                <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Settings</p>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { to: '/app/profile', label: 'Profile & Account', icon: UserCircle, ownerOnly: false },
                    { to: '/app/settings/credentials', label: 'Credentials', icon: KeyRound, ownerOnly: false },
                    { to: '/app/settings/workspace', label: 'Workspace', icon: Settings, ownerOnly: false },
                    { to: '/app/settings/email', label: 'Email Prefs', icon: Mail, ownerOnly: false },
                    { to: '/app/knowledge-base', label: 'Help', icon: BookOpen, ownerOnly: false },
                    { to: '/app/settings/ai-providers', label: 'AI Gateway', icon: BrainCircuit, ownerOnly: false },
                    { to: '/app/settings/subscriptions', label: 'Billing', icon: Gauge, ownerOnly: false },
                    { to: '/app/customer-portal', label: 'Customer Portal', icon: Users, ownerOnly: true },
                    { to: '/app/settings/virtual-assistant', label: 'Assistant', icon: Sparkles, ownerOnly: false },
                    { to: '/app/settings/assistant-protocol', label: 'Protocol', icon: ShieldCheck, ownerOnly: false },
                    { to: '/app/settings/remote-agents', label: 'Remote Agents', icon: MonitorSmartphone, ownerOnly: false, agentId: 'nav.remote-agents' },
                    { to: '/app/settings/whatsapp', label: 'WhatsApp', icon: MessageCircleMore, ownerOnly: false },
                    { to: '/app/settings/affiliate-links', label: 'Affiliate', icon: Link2, ownerOnly: false },
                    { to: '/app/graphs', label: 'Knowledge Graph', icon: Network, ownerOnly: false, agentId: 'nav.graphs' },
                  ].filter(item => !item.ownerOnly || user?.is_owner).map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.to}
                        onClick={() => { navigate(item.to); setUserDropdown(false); }}
                        role="menuitem"
                        data-agent-id={item.agentId}
                        className="flex flex-col items-center gap-1 rounded-lg border border-slate-200 p-2.5 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                      >
                        <Icon size={18} aria-hidden="true" />
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* Theme toggle */}
              <div className="border-b border-slate-200 p-3 dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Theme</span>
                  <div className="flex gap-1">
                    <button onClick={() => { setTheme('light'); }} className={`rounded-lg p-2 ${theme === 'light' ? 'bg-brand-600 text-white' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'}`} aria-label="Light theme"><Sun size={16} /></button>
                    <button onClick={() => { setTheme('dark'); }} className={`rounded-lg p-2 ${theme === 'dark' ? 'bg-brand-600 text-white' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'}`} aria-label="Dark theme"><Moon size={16} /></button>
                  </div>
                </div>
              </div>
              {/* Sign out */}
              <button
                onClick={() => { logout(); setUserDropdown(false); }}
                role="menuitem"
                className="flex w-full items-center gap-2 rounded-b-lg px-3 py-2.5 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-slate-700 cursor-pointer"
              >
                <LogOut size={16} /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
